import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button, Card } from "@payflow/ui";
import { useTenantTheme } from "../../contexts/TenantThemeContext";
import { applyThemeColors, isValidHex, generateShades } from "../../utils/color-utils";

export function SettingsThemePage() {
  const { t } = useTranslation();
  const { branding, updateTheme, isLoading } = useTenantTheme();

  const [corPrimaria, setCorPrimaria] = useState(branding?.corPrimaria || "#6D49FF");
  const [corSecundaria, setCorSecundaria] = useState(branding?.corSecundaria || "#D4AF37");
  const [idioma, setIdioma] = useState(branding?.idioma || "pt-BR");
  const [logoPreview, setLogoPreview] = useState<string | null>(branding?.logoUrl || null);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (branding) {
      setCorPrimaria(branding.corPrimaria);
      setCorSecundaria(branding.corSecundaria);
      setIdioma(branding.idioma || "pt-BR");
      setLogoPreview(branding.logoUrl || null);
    }
  }, [branding]);

  // Live preview
  useEffect(() => {
    if (isValidHex(corPrimaria)) {
      applyThemeColors(corPrimaria, isValidHex(corSecundaria) ? corSecundaria : undefined);
    }
  }, [corPrimaria, corSecundaria]);

  const handleSave = async () => {
    if (!isValidHex(corPrimaria) || !isValidHex(corSecundaria)) {
      setMessage({ type: "error", text: t("settings.theme.invalid_color") });
      return;
    }

    setIsSaving(true);
    setMessage(null);
    try {
      await updateTheme({ corPrimaria, corSecundaria, idioma });
      setMessage({ type: "success", text: t("settings.theme.saved") });
    } catch (err) {
      setMessage({ type: "error", text: err instanceof Error ? err.message : t("settings.theme.save_error") });
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      setMessage({ type: "error", text: t("settings.theme.logo_too_large") });
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result as string;
      setLogoPreview(dataUrl);

      try {
        await updateTheme({ logoUrl: dataUrl } as any);
        setMessage({ type: "success", text: t("settings.theme.logo_saved") });
      } catch {
        setMessage({ type: "error", text: t("settings.theme.logo_error") });
      }
    };
    reader.readAsDataURL(file);
  };

  const handleReset = () => {
    const defaultPrimary = "#6D49FF";
    const defaultAccent = "#D4AF37";
    setCorPrimaria(defaultPrimary);
    setCorSecundaria(defaultAccent);
    applyThemeColors(defaultPrimary, defaultAccent);
  };

  const primaryShades = isValidHex(corPrimaria) ? generateShades(corPrimaria) : null;

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">{t("settings.theme.title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("settings.theme.description")}</p>
      </div>

      {message && (
        <div
          className={`rounded-lg px-4 py-3 text-sm ${
            message.type === "success"
              ? "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400"
              : "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400"
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Colors */}
      <Card>
        <div className="p-6">
          <h2 className="mb-4 text-lg font-semibold text-foreground">{t("settings.theme.colors")}</h2>

          <div className="grid gap-6 sm:grid-cols-2">
            {/* Primary Color */}
            <div>
              <label className="mb-2 block text-sm font-medium text-foreground">
                {t("settings.theme.primary_color")}
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={corPrimaria}
                  onChange={(e) => setCorPrimaria(e.target.value)}
                  className="h-10 w-14 cursor-pointer rounded border border-border"
                />
                <input
                  type="text"
                  value={corPrimaria}
                  onChange={(e) => {
                    const val = e.target.value;
                    setCorPrimaria(val);
                  }}
                  placeholder="#6D49FF"
                  className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                />
              </div>
            </div>

            {/* Accent Color */}
            <div>
              <label className="mb-2 block text-sm font-medium text-foreground">
                {t("settings.theme.accent_color")}
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={corSecundaria}
                  onChange={(e) => setCorSecundaria(e.target.value)}
                  className="h-10 w-14 cursor-pointer rounded border border-border"
                />
                <input
                  type="text"
                  value={corSecundaria}
                  onChange={(e) => setCorSecundaria(e.target.value)}
                  placeholder="#D4AF37"
                  className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                />
              </div>
            </div>
          </div>

          {/* Shade Preview */}
          {primaryShades && (
            <div className="mt-6">
              <label className="mb-2 block text-sm font-medium text-muted-foreground">
                {t("settings.theme.shades_preview")}
              </label>
              <div className="flex gap-1 overflow-x-auto">
                {Object.entries(primaryShades).map(([shade, color]) => (
                  <div key={shade} className="flex flex-col items-center">
                    <div
                      className="h-10 w-10 rounded"
                      style={{ backgroundColor: color }}
                    />
                    <span className="mt-1 text-xs text-muted-foreground">{shade}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Logo */}
      <Card>
        <div className="p-6">
          <h2 className="mb-4 text-lg font-semibold text-foreground">{t("settings.theme.logo")}</h2>

          <div className="flex items-start gap-6">
            {/* Logo Preview */}
            <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-lg border border-border bg-muted">
              {logoPreview ? (
                <img src={logoPreview} alt="Logo" className="h-full w-full object-contain" />
              ) : (
                <span className="text-3xl font-bold text-primary-500">
                  {branding?.nome?.charAt(0) || "P"}
                </span>
              )}
            </div>

            <div className="flex-1">
              <p className="mb-3 text-sm text-muted-foreground">
                {t("settings.theme.logo_hint")}
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml"
                onChange={handleLogoUpload}
                className="hidden"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                {t("settings.theme.upload_logo")}
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Idioma do app mobile */}
      <Card>
        <div className="p-6 space-y-4">
          <div>
            <h3 className="text-lg font-bold text-foreground">
              {t("settings.theme.mobile_language")}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {t("settings.theme.mobile_language_hint")}
            </p>
          </div>
          <select
            value={idioma}
            onChange={(e) => setIdioma(e.target.value)}
            className="w-full max-w-xs rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary-400"
          >
            <option value="pt-BR">Português (Brasil)</option>
            <option value="pt-PT">Português (Portugal)</option>
            <option value="en">English</option>
          </select>
        </div>
      </Card>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={handleReset}>
          {t("settings.theme.reset")}
        </Button>

        <Button onClick={handleSave} disabled={isSaving || isLoading}>
          {isSaving ? t("common.saving") : t("common.save")}
        </Button>
      </div>
    </div>
  );
}
